import { useCallback } from 'react';

import {
  ErrorResponse,
  PaginatedRequest,
  PaginatedResponse,
  ValidationErrorResponse,
} from '@opositandopn/http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { useAxios } from '../../../context/AxiosContext';
import { FALLBACK_ERROR_CODE } from '../../../utils/api';
import { getIanaTimeZoneId } from '../../../utils/date';
import {
  AuditViewModel,
  DifficultyLevel,
  ExplainQuestionAttemptCommandResult,
  LessonViewModel,
  MultipleChoiceQuestionViewModel,
  QuestionAttemptViewModel,
  SchoolViewModel,
  Status,
  SubjectViewModel,
  TagViewModel,
  UnitViewModel,
  Visibility,
} from '../../questions/api/useMultipleChoiceQuestions';

/** How the user answers: on screen, or on paper and transcribed at the end. */
export type AttemptMode = 'interactive' | 'manual';

export type AttemptStatus =
  | 'created'
  | 'in_progress'
  | 'paused'
  | 'submitting'
  | 'submitted'
  | 'overdue'
  | 'discarded';

export interface AttemptScoreViewModel {
  score: number;
  date: Date;
}

export interface AssessmentPreviewModel<T> {
  id: string;
  name: string;
  questions: T[];
  subject: SubjectViewModel;
  unit?: UnitViewModel;
  lesson?: LessonViewModel;
  status: Status;
  visibility: Visibility;
  difficultyLevel: DifficultyLevel;
  /** In seconds. */
  timeLimit: number;
  /**
   * The cut-off the assessment declares, if any — the list carries it so a card can
   * read it without fetching the assessment itself.
   */
  cutoffScore?: number;
  releaseDate: Date;
  attemptScores?: AttemptScoreViewModel[];
}

export interface AssessmentViewModel<T> extends AssessmentPreviewModel<T> {
  description?: string;
  purpose: Purpose;
  referenceStandard: ReferenceStandard;
  methodology: Methodology;
  perspective: Perspective;
  setting: Setting;
  tags: TagViewModel[];
  imageUrl?: string;
  maintainsQuestionOrder: boolean;
  official: boolean;
  auditLog: AuditViewModel[];
}

export interface MultipleChoiceAssessmentViewModel extends AssessmentViewModel<MultipleChoiceQuestionViewModel> {
  numberOfChoices: number;
}

export type Purpose = 'formative' | 'summative' | 'diagnostic';

export type ReferenceStandard = 'criterion-referenced' | 'norm-referenced';

export type Methodology = 'qualitative' | 'quantitative' | 'blended';

export type Perspective =
  | 'self-assessment'
  | 'peer-assessment'
  | 'instructor'
  | 'automated';

export type Setting = 'standardized' | 'informal' | 'formal';

export interface CreateMultipleChoiceAssessmentRequest {
  subjectId: string;
  unitId?: string;
  lessonId?: string;
  name: string;
  description: string;
  timeLimit: number;
  visibility: Visibility;
  purpose: Purpose;
  referenceStandard: ReferenceStandard;
  methodology: Methodology;
  perspective: Perspective;
  setting: Setting;
  numberOfChoices: number;
  maintainsQuestionOrder?: boolean;
  official?: boolean;
  releaseDate?: Date;
  tagIds?: string[];
  base64Image?: string;
  cutoffScore?: number;
}

export interface CreateMultipleChoiceAssessmentResponse {
  assessmentId: string;
}

export const useCreateMultipleChoiceAssessment = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(
    request: CreateMultipleChoiceAssessmentRequest,
  ): Promise<CreateMultipleChoiceAssessmentResponse> {
    const response =
      await instance.post<CreateMultipleChoiceAssessmentResponse>(
        `/v2/subjects/${request.subjectId}/assessments/mc`,
        request,
      );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceAssessmentResponse,
    AxiosError<ValidationErrorResponse>,
    CreateMultipleChoiceAssessmentRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessments'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export interface UpdateMultipleChoiceAssessmentRequest {
  id: string;
  subjectId: string;
  unitId?: string;
  lessonId?: string;
  name?: string;
  description?: string;
  timeLimit?: number;
  visibility?: Visibility;
  purpose?: Purpose;
  referenceStandard?: ReferenceStandard;
  methodology?: Methodology;
  perspective?: Perspective;
  setting?: Setting;
  maintainsQuestionOrder?: boolean;
  official?: boolean;
  releaseDate?: Date;
  tagIds?: string[];
  base64Image?: string;
  cutoffScore?: number;
  status?: Status;
}

export const useUpdateMultipleChoiceAssessment = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateMultipleChoiceAssessmentRequest) {
    const response = await instance.patch(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.id}`,
      request,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    UpdateMultipleChoiceAssessmentRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessment'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessments'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export type GetMultipleChoiceAssessmentsRequest = PaginatedRequest & {
  subjectId: string;
  params?: {
    text?: string;
    statuses?: Status[];
    visibilities?: Visibility[];
    purposes?: Purpose[];
    referenceStandards?: ReferenceStandard[];
    methodologies?: Methodology[];
    perspectives?: Perspective[];
    settings?: Setting[];
    difficultyLevels?: DifficultyLevel[];
    tagIds?: string[];
    questionIds?: string[];
    numberOfChoices?: number;
    official?: boolean;
    released?: boolean;
  };
};

export const useGetMultipleChoiceAssessments = (
  request: GetMultipleChoiceAssessmentsRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<
    PaginatedResponse<AssessmentPreviewModel<string>>
  > {
    const response = await instance.get<
      PaginatedResponse<AssessmentPreviewModel<string>>
    >(`/v2/subjects/${request.subjectId}/assessments/mc`, {
      params: {
        fromTime: request.fromTime,
        toTime: request.toTime,
        pageNumber: request.pageNumber,
        pageSize: request.pageSize,
        sort: request.sort,
        text: request.params?.text,
        status: request.params?.statuses,
        visibility: request.params?.visibilities,
        purpose: request.params?.purposes,
        referenceStandard: request.params?.referenceStandards,
        methodology: request.params?.methodologies,
        perspective: request.params?.perspectives,
        setting: request.params?.settings,
        difficultyLevel: request.params?.difficultyLevels,
        tagId: request.params?.tagIds,
        questionId: request.params?.questionIds,
        numberOfChoices: request.params?.numberOfChoices,
        official: request.params?.official,
        released: request.params?.released,
      },
      paramsSerializer: {
        indexes: null,
      },
    });

    return response.data;
  }

  return useQuery({
    queryKey: ['getMultipleChoiceAssessments', request],
    queryFn,
    enabled,
  });
};

export type GetMultipleChoiceAssessmentRequest = {
  subjectId: string;
  assessmentId: string;
};

export const useGetMultipleChoiceAssessment = (
  request: GetMultipleChoiceAssessmentRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<MultipleChoiceAssessmentViewModel> {
    const response = await instance.get<MultipleChoiceAssessmentViewModel>(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: [
      'getMultipleChoiceAssessment',
      request.subjectId,
      request.assessmentId,
    ],
    queryFn,
    enabled,
  });
};

export type GetMultipleChoiceAssessmentMetricsRequest = {
  subjectId: string;
  assessmentId: string;
};

export type AssessmentMetricsViewModel = {
  averageTime: number;
  averageGrade: number;
  cutoffScore: number;
  /**
   * How many sat the assessment, as the API reports it — nought until somebody has. Sent
   * whether or not the reader is on the board themselves, and it does not move the day
   * they first sit it.
   */
  total: number;
  position?: number;
  /**
   * How far down the field the reader sits, from just above 0 at the top to 1 at the
   * foot. `position` and `total` cannot answer that between them: the API reports the
   * leading positions as they stand and inflates the rest, so the two are not on one
   * scale. Absent when they have not sat the assessment.
   */
  positionShare?: number;
  /**
   * The reader's own ranked attempt, whatever their position — `scores` only reaches
   * the fifth. Absent when they have not sat the assessment.
   */
  ownScore?: ScoreViewModel;
  scores: ScoreViewModel[];
};

export type ScoreViewModel = {
  grade: number;
  elapsedTime: number;
  submittedAt: Date;
  username?: string;
  school?: SchoolViewModel;
};

export const useGetMultipleChoiceAssessmentMetrics = (
  request: GetMultipleChoiceAssessmentMetricsRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<AssessmentMetricsViewModel> {
    const response = await instance.get<AssessmentMetricsViewModel>(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}/metrics`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: ['getMultipleChoiceAssessmentMetrics', request],
    queryFn,
    enabled,
  });
};

export type ResetMultipleChoiceAssessmentMetricsRequest = {
  subjectId: string;
  assessmentId: string;
};

export const useResetMultipleChoiceAssessmentMetrics = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(
    request: ResetMultipleChoiceAssessmentMetricsRequest,
  ) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}/metrics/reset`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    ResetMultipleChoiceAssessmentMetricsRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessmentMetrics'],
      });
    },
    meta: {
      errorMessages: {
        not_found: {
          title: '¡Ups!',
          message: 'Este simulacro todavía no tiene estadísticas que borrar.',
        },
        assessment_already_released: {
          title: 'Operación inválida',
          message:
            'Este simulacro está visible. Sus estadísticas no se pueden borrar.',
          variant: 'warning',
        },
      },
    },
  });
};

export type DeleteMultipleChoiceAssessmentRequest = {
  subjectId: string;
  assessmentId: string;
};

export const useDeleteMultipleChoiceAssessment = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DeleteMultipleChoiceAssessmentRequest) {
    const response = await instance.delete(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}`,
    );

    return response.data;
  }

  function forgetAssessment(request: DeleteMultipleChoiceAssessmentRequest) {
    queryClient.invalidateQueries({
      queryKey: [
        'getMultipleChoiceAssessment',
        request.subjectId,
        request.assessmentId,
      ],
      refetchType: 'none',
    });
    queryClient.invalidateQueries({
      queryKey: ['getMultipleChoiceAssessments'],
    });
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    DeleteMultipleChoiceAssessmentRequest
  >({
    mutationFn,
    onSuccess: (_, request) => {
      forgetAssessment(request);
    },
    onError: (e: AxiosError<ErrorResponse>, request) => {
      if (e.response?.data.code === 'invalid_operation') {
        forgetAssessment(request);
      }
    },
    meta: {
      errorMessages: {
        invalid_operation: {
          title: 'Operación inválida',
          message:
            'Este simulacro ya no está disponible. Se ha actualizado el listado.',
          variant: 'warning',
        },
        [FALLBACK_ERROR_CODE]: {
          title: '¡Ups!',
          message:
            'Parece que ha ocurrido un error al eliminar el simulacro. Por favor, inténtalo de nuevo más tarde.',
        },
      },
    },
  });
};

export interface AddQuestionToAssessmentRequest {
  subjectId: string;
  assessmentId: string;
  questionId: string;
}

export const useAddQuestionToMultipleChoiceAssessment = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: AddQuestionToAssessmentRequest) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}/questions`,
      {
        questionId: request.questionId,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    AddQuestionToAssessmentRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessment'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessments'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export interface RemoveQuestionFromAssessmentRequest {
  subjectId: string;
  assessmentId: string;
  questionId: string;
}

export const useRemoveQuestionFromMultipleChoiceAssessment = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: RemoveQuestionFromAssessmentRequest) {
    const response = await instance.delete(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}/questions/${request.questionId}`,
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    RemoveQuestionFromAssessmentRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessment'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessments'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceQuestions'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export interface UpdateAssessmentQuestionRequest {
  subjectId: string;
  assessmentId: string;
  questionId: string;
  index: number;
}

export const useUpdateMultipleChoiceAssessmentQuestion = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: UpdateAssessmentQuestionRequest) {
    const response = await instance.patch(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}/questions/${request.questionId}`,
      {
        index: request.index,
      },
    );

    return response.data;
  }

  return useMutation<
    void,
    AxiosError<ValidationErrorResponse>,
    UpdateAssessmentQuestionRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessment'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessments'],
      });
    },
    meta: {
      errorMessages: {
        invalid_operation: { title: 'Operación inválida' },
      },
    },
  });
};

export type CreateMultipleChoiceQuestionAttemptCommandResult = {
  questionId: string;
  id: string;
  text: string;
  choices: {
    id: string;
    text: string;
  }[];
  /** Id of the choice already picked, or an empty string when still unanswered. */
  answer: string;
  imageUrl?: string;
  bookmarked?: boolean;
  flagged?: boolean;
};

/**
 * An attempt the user can work on, whether it has just been created or is being carried
 * on after a pause: the questions as the user was shown them, the answers given so far,
 * and the time left according to the server.
 */
export type CreateMultipleChoiceAssessmentAttemptCommandResult = {
  id: string;
  assessmentId: string;
  name: string;
  description?: string;
  timeLimit: number;
  /** Seconds left before the time limit runs out — the whole limit for a new attempt. */
  remainingTime: number;
  elapsedTime: number;
  canPause: boolean;
  status: AttemptStatus;
  questionAttempts: CreateMultipleChoiceQuestionAttemptCommandResult[];
  imageUrl?: string;
  difficultyLevel: DifficultyLevel;
  subject: SubjectViewModel;
};

export type CreateMultipleChoiceAssessmentAttemptRequest = {
  subjectId: string;
  assessmentId: string;
  mode?: AttemptMode;
};

export const useCreateMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();

  async function mutationFn(
    request: CreateMultipleChoiceAssessmentAttemptRequest,
  ) {
    const timeZoneId = getIanaTimeZoneId();
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/${request.assessmentId}/attempt`,
      { timeZoneId, mode: request.mode },
    );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceAssessmentAttemptCommandResult,
    AxiosError<ErrorResponse>,
    CreateMultipleChoiceAssessmentAttemptRequest
  >({
    mutationFn,
  });
};

export type CreateRandomMultipleChoiceAssessmentAttemptRequest = {
  subjectId: string;
  numberOfQuestions?: number;
  numberOfChoices?: number;
  unitIds?: string[];
  lessonIds?: string[];
  difficultyLevels?: DifficultyLevel[];
  mode?: AttemptMode;
};

export const useCreateRandomMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();

  async function mutationFn(
    request: CreateRandomMultipleChoiceAssessmentAttemptRequest,
  ) {
    const timeZoneId = getIanaTimeZoneId();
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/random/attempt`,
      {
        numberOfQuestions: request.numberOfQuestions,
        numberOfChoices: request.numberOfChoices,
        unitIds: request.unitIds,
        lessonIds: request.lessonIds,
        difficultyLevels: request.difficultyLevels,
        timeZoneId,
        mode: request.mode,
      },
    );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceAssessmentAttemptCommandResult,
    AxiosError<ErrorResponse>,
    CreateRandomMultipleChoiceAssessmentAttemptRequest
  >({
    mutationFn,
    meta: {
      errorMessages: {
        not_found: {
          title: '¡Ups!',
          message:
            'No se han encontrado preguntas para los temas seleccionados',
        },
      },
    },
  });
};

export type CreateExamMultipleChoiceAssessmentAttemptRequest = {
  subjectId: string;
  numberOfChoices?: number;
  difficultyLevels?: DifficultyLevel[];
  mode?: AttemptMode;
};

export const useCreateExamMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();

  async function mutationFn(
    request: CreateExamMultipleChoiceAssessmentAttemptRequest,
  ) {
    const timeZoneId = getIanaTimeZoneId();
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/exam/attempt`,
      {
        numberOfChoices: request.numberOfChoices,
        difficultyLevels: request.difficultyLevels,
        timeZoneId,
        mode: request.mode,
      },
    );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceAssessmentAttemptCommandResult,
    AxiosError<ErrorResponse>,
    CreateExamMultipleChoiceAssessmentAttemptRequest
  >({
    mutationFn,
    meta: {
      errorMessages: {
        not_found: {
          title: '¡Ups!',
          message:
            'No se han encontrado preguntas para los temas seleccionados',
        },
      },
    },
  });
};

export type CreateMultipleChoiceAssessmentAttemptFromMissedQuestionsRequest = {
  subjectId: string;
  numberOfQuestions?: number;
  mode?: AttemptMode;
};

export const useCreateMultipleChoiceAssessmentAttemptFromMissedQuestions =
  () => {
    const { instance } = useAxios();

    async function mutationFn(
      request: CreateMultipleChoiceAssessmentAttemptFromMissedQuestionsRequest,
    ) {
      const timeZoneId = getIanaTimeZoneId();
      const response = await instance.post(
        `/v2/subjects/${request.subjectId}/assessments/mc/missed/attempt`,
        {
          numberOfQuestions: request.numberOfQuestions,
          timeZoneId,
          mode: request.mode,
        },
      );

      return response.data;
    }

    return useMutation<
      CreateMultipleChoiceAssessmentAttemptCommandResult,
      AxiosError<ErrorResponse>,
      CreateMultipleChoiceAssessmentAttemptFromMissedQuestionsRequest
    >({
      mutationFn,
    });
  };

export type CreateMultipleChoiceAssessmentAttemptFromBookmarkedQuestionsRequest =
  {
    subjectId: string;
    numberOfQuestions?: number;
    mode?: AttemptMode;
  };

export const useCreateMultipleChoiceAssessmentAttemptFromBookmarkedQuestions =
  () => {
    const { instance } = useAxios();

    async function mutationFn(
      request: CreateMultipleChoiceAssessmentAttemptFromBookmarkedQuestionsRequest,
    ) {
      const timeZoneId = getIanaTimeZoneId();
      const response = await instance.post(
        `/v2/subjects/${request.subjectId}/assessments/mc/bookmarked/attempt`,
        {
          numberOfQuestions: request.numberOfQuestions,
          timeZoneId,
          mode: request.mode,
        },
      );

      return response.data;
    }

    return useMutation<
      CreateMultipleChoiceAssessmentAttemptCommandResult,
      AxiosError<ErrorResponse>,
      CreateMultipleChoiceAssessmentAttemptFromBookmarkedQuestionsRequest
    >({
      mutationFn,
    });
  };

export type CreateSelfMultipleChoiceAssessmentAttemptRequest = {
  subjectId: string;
  mode?: AttemptMode;
};

export const useCreateSelfMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();

  async function mutationFn(
    request: CreateSelfMultipleChoiceAssessmentAttemptRequest,
  ) {
    const timeZoneId = getIanaTimeZoneId();
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/self/attempt`,
      { timeZoneId, mode: request.mode },
    );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceAssessmentAttemptCommandResult,
    AxiosError<ErrorResponse>,
    CreateSelfMultipleChoiceAssessmentAttemptRequest
  >({
    mutationFn,
  });
};

export type SubmitQuestionAttemptRequest = {
  id: string;
  answer: string;
  flagged: boolean;
};

export type SubmitAssessmentAttemptRequest = {
  subjectId: string;
  assessmentAttemptId: string;
  questionAttempts: SubmitQuestionAttemptRequest[];
};

export type AssessmentAttemptViewModel<T> = {
  assessmentId: string;
  name: string;
  status: Status;
  official: boolean;
  score: number;
  scorePenalty: number;
  cutoffScore?: number;
  elapsedTime: number;
  questionAttempts: QuestionAttemptViewModel<T>[];
  timeZoneId?: string;
};

/**
 * Grades the attempt. Also what an attempt answered on paper submits to once its answers
 * are transcribed — it is the attempt's `submitting` state that makes the elapsed time
 * count the sitting rather than the time spent typing the answers in.
 */
export const useSubmitMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: SubmitAssessmentAttemptRequest) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/submit`,
      {
        questionAttempts: request.questionAttempts,
      },
    );

    return response.data;
  }

  return useMutation<
    AssessmentAttemptViewModel<MultipleChoiceQuestionViewModel>,
    AxiosError<ErrorResponse>,
    SubmitAssessmentAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessmentAttempt'],
      });
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessmentAttempts'],
      });
    },
  });
};

export type BeginManualSubmissionAssessmentAttemptRequest = {
  subjectId: string;
  assessmentAttemptId: string;
};

/**
 * Ends the sitting of an attempt answered on paper and moves it to `submitting`, which
 * stops the server's clock while the answers are transcribed. Only `in_progress`
 * attempts may begin submission, so the server rejects a second call.
 */
export const useBeginManualSubmissionMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(
    request: BeginManualSubmissionAssessmentAttemptRequest,
  ) {
    await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/begin-manual-submission`,
    );
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    BeginManualSubmissionAssessmentAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessmentAttempts'],
      });
    },
  });
};

export type ScanAssessmentAttemptAnswerSheetRequest = {
  subjectId: string;
  assessmentAttemptId: string;
  base64Image: string;
};

export type ScannedQuestionAttemptViewModel = {
  /** The question's 1-based position in the assessment. */
  number: number;
  /** 1-based choice, 0 when the question was left blank. */
  answerIndex: number;
  /**
   * Whether the scan wants a person to look at this question before the attempt is
   * submitted — two marks where one was allowed, or a mark too faint to call.
   */
  requiresReview: boolean;
};

export type ScanAssessmentAttemptAnswerSheetCommandResult = {
  questionAttempts: ScannedQuestionAttemptViewModel[];
};

/**
 * Reads the answers off a photo of the paper answer sheet. Nothing is stored — the
 * answers come back for the user to check and correct before the attempt is submitted.
 */
export const useScanMultipleChoiceAssessmentAttemptAnswerSheet = () => {
  const { instance } = useAxios();

  async function mutationFn(request: ScanAssessmentAttemptAnswerSheetRequest) {
    const response = await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/scan-answer-sheet`,
      {
        base64Image: request.base64Image,
      },
    );

    return response.data;
  }

  return useMutation<
    ScanAssessmentAttemptAnswerSheetCommandResult,
    AxiosError<ErrorResponse>,
    ScanAssessmentAttemptAnswerSheetRequest
  >({
    mutationFn,
    meta: {
      // The API refuses a photo it cannot stand behind rather than answering with
      // marks read off it, and says which thing is wrong. Each `sheet_` code gets
      // the one instruction that fixes it — a photo the user can retake knowing
      // what to change, instead of "inténtalo de nuevo" over and over.
      //
      // Four of these codes name a single failed check rather than the general
      // advice its neighbours share, because that advice would send the user to do
      // the wrong thing: a compressed re-share is not a photo taken too far away, a
      // crop of one strip of the page is not a photo taken too far away either, and
      // a patterned tablecloth read as print is not dim light. They sit next to the
      // code they refine.
      errorMessages: {
        sheet_resolution_too_low: {
          title: 'La plantilla se ve muy pequeña',
          message:
            'Acerca la cámara hasta que la plantilla ocupe toda la foto y vuelve a intentarlo.',
        },
        sheet_photo_downscaled: {
          title: 'La foto está comprimida',
          message:
            'Parece una copia reenviada, no la original. Súbela desde tu galería tal y como la hizo la cámara.',
        },
        sheet_photo_not_a_sheet: {
          title: 'La foto no es de la hoja entera',
          message:
            'Parece un recorte. Haz la foto de la hoja completa, no de una tira de ella.',
        },
        sheet_image_too_blurry: {
          title: 'La foto está desenfocada',
          message:
            'Vuelve a hacerla con la plantilla enfocada, sin mover el móvil.',
        },
        sheet_header_not_found: {
          title: 'No hemos encontrado la plantilla',
          message:
            'Asegúrate de que la hoja entera salga en la foto, cabecera incluida.',
        },
        sheet_geometry_unreliable: {
          title: 'La hoja sale torcida',
          message:
            'Ponla lisa sobre una superficie plana y haz la foto desde arriba, de frente.',
        },
        sheet_corner_marks_missing: {
          title: 'Faltan las esquinas de la plantilla',
          message:
            'No se ven las cuatro marcas de las esquinas. Encuadra la hoja entera y vuelve a intentarlo.',
        },
        sheet_answer_zone_clipped: {
          title: 'La foto corta las respuestas',
          message: 'Encuadra la hoja entera y vuelve a intentarlo.',
        },
        sheet_grid_not_legible: {
          title: 'No se distingue la cuadrícula',
          message:
            'Repite la foto con luz uniforme, sin sombras ni reflejos sobre la hoja.',
        },
        sheet_background_patterned: {
          title: 'El fondo se cuela en la lectura',
          message:
            'Pon la hoja sobre una superficie lisa y de un solo color, sin dibujos ni sombras cruzándola.',
        },
        sheet_answer_zone_not_found: {
          title: 'No hemos encontrado las respuestas',
          message:
            'Comprueba que sea la plantilla de respuestas de OpositandoPN y que salga entera.',
        },
        sheet_grid_not_reconstructed: {
          title: 'No hemos podido leer la cuadrícula',
          message:
            'Pon la hoja lisa sobre una superficie plana y haz la foto de frente.',
        },
        sheet_marks_unclear: {
          title: 'Las marcas se distinguen poco',
          message:
            'Repite la foto con luz uniforme, y repasa las marcas si quedaron muy flojas.',
        },
        // Comparte causa con `sheet_marks_unclear` en la API, y aun así dice lo
        // contrario: aquí la foto puede estar perfecta y las respuestas que sí se
        // han leído ser correctas. Lo que sobra son las que han quedado sin
        // decidir, así que repetir la foto puede no cambiar nada — un lápiz flojo
        // sigue estando flojo — y es la única negativa que ofrece una salida en
        // vez de otro intento.
        sheet_too_many_to_check: {
          title: 'Hay demasiadas respuestas por revisar',
          message:
            'No hemos podido leer con seguridad buena parte de la plantilla. Repite la foto repasando las marcas más flojas, o rellena esas respuestas a mano.',
        },
        sheet_outline_not_found: {
          title: 'No hemos encontrado los bordes de la hoja',
          message:
            'Ponla lisa sobre una superficie de color distinto al del papel, con luz uniforme y sin reflejos cruzándola.',
        },
        sheet_not_readable: {
          title: 'No hemos podido leer la plantilla',
          message:
            'Repite la foto con la hoja lisa, enfocada y con luz uniforme.',
        },
        // El único código de esta lista que no habla de la foto. Leer una
        // plantilla ocupa el único núcleo de la instancia, así que las lecturas
        // van de una en una y esta no encontró hueco a tiempo. La foto puede
        // ser perfecta: pedir que la repita — que es lo que dice el mensaje
        // genérico — la mandaría a arreglar algo que no está roto.
        sheet_scanner_busy: {
          title: 'Estamos leyendo otra plantilla',
          message:
            'Hay otra lectura en marcha ahora mismo. Espera unos segundos y vuelve a enviar la misma foto.',
        },
        [FALLBACK_ERROR_CODE]: {
          title: '¡Ups!',
          message:
            'Parece que ha ocurrido un error al leer la plantilla de respuestas. Revisa la foto e inténtalo de nuevo.',
        },
      },
    },
  });
};

export type PauseQuestionAttemptRequest = {
  id: string;
  answer: string;
  flagged: boolean;
};

export type PauseAssessmentAttemptRequest = {
  subjectId: string;
  assessmentAttemptId: string;
  questionAttempts: PauseQuestionAttemptRequest[];
};

export type ResumeAssessmentAttemptRequest = {
  subjectId: string;
  assessmentAttemptId: string;
};

/**
 * Pausing stops the clock and stores the answers given so far. It reports no time of
 * its own — resuming hands back the attempt with the server's remaining time.
 */
export const usePauseMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: PauseAssessmentAttemptRequest) {
    await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/pause`,
      {
        questionAttempts: request.questionAttempts,
      },
    );
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    PauseAssessmentAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessmentAttempts'],
      });
    },
  });
};

/**
 * Resuming restarts the countdown and hands back the attempt in the same shape a freshly
 * created one comes in, so it is everything needed to carry it on in a single round trip.
 */
export const useResumeMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: ResumeAssessmentAttemptRequest) {
    const response =
      await instance.post<CreateMultipleChoiceAssessmentAttemptCommandResult>(
        `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/resume`,
      );

    return response.data;
  }

  return useMutation<
    CreateMultipleChoiceAssessmentAttemptCommandResult,
    AxiosError<ErrorResponse>,
    ResumeAssessmentAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessmentAttempts'],
      });
    },
  });
};

export type GetAssessmentAttemptTimeRequest = {
  subjectId: string;
  assessmentAttemptId: string;
};

export type AssessmentAttemptTimeViewModel = {
  remainingTime: number;
};

/**
 * A request left hanging by a dead connection is abandoned rather than allowed to hold
 * the countdown back from asking again — by the time it would arrive the answer is too
 * stale for the countdown to place, and it discards anything this slow anyway.
 */
const ATTEMPT_TIME_TIMEOUT_MS = 5000;

/**
 * Reads what the server makes of an attempt's clock, without any of the questions. Not a
 * query hook: the countdown polls this itself so it can time the round trip, and holding
 * the answer in react-query would re-render the assessment twice for a value that is read
 * once and thrown away. Silent, because a poll that fails simply runs again and there is
 * nothing for the user to do about it in the meantime.
 */
export const useReadMultipleChoiceAssessmentAttemptTime = () => {
  const { instance } = useAxios();

  return useCallback(
    async (request: GetAssessmentAttemptTimeRequest) => {
      const response = await instance.get<AssessmentAttemptTimeViewModel>(
        `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/time`,
        { silent: true, timeout: ATTEMPT_TIME_TIMEOUT_MS },
      );

      return response.data;
    },
    [instance],
  );
};

export type DiscardAssessmentAttemptRequest = {
  subjectId: string;
  assessmentAttemptId: string;
};

/**
 * Throws an attempt away: it stops showing up as something to carry on with and can no
 * longer be resumed or submitted.
 */
export const useDiscardMultipleChoiceAssessmentAttempt = () => {
  const { instance } = useAxios();
  const queryClient = useQueryClient();

  async function mutationFn(request: DiscardAssessmentAttemptRequest) {
    await instance.post(
      `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/discard`,
    );
  }

  return useMutation<
    void,
    AxiosError<ErrorResponse>,
    DiscardAssessmentAttemptRequest
  >({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['getMultipleChoiceAssessmentAttempts'],
      });
    },
  });
};

export type GetMultipleChoiceAssessmentAttemptsRequest = PaginatedRequest & {
  subjectId: string;
  params?: {
    assessmentId?: string;
    statuses?: AttemptStatus[];
  };
};

export type AssessmentAttemptPreviewModel = {
  id: string;
  name: string;
  elapsedTime: number;
  status: string;
  createdAt: Date;
  subject: SubjectViewModel;
  units?: UnitViewModel[];
  lessons?: LessonViewModel[];
  score?: number;
  questionAttempts: QuestionAttemptsViewModel;
  questionAttemptsSummary?: QuestionAttemptsSummaryViewModel;
  timeZoneId?: string;
  /** Absent on attempts served by an API that predates the mode. */
  mode?: AttemptMode;
};

/**
 * How far through the questions an attempt got. Counts answers rather than
 * corrections, so it also describes an attempt that has not been submitted.
 */
export type QuestionAttemptsViewModel = {
  answered: number;
  total: number;
};

export type QuestionAttemptsSummaryViewModel = {
  correct: number;
  incorrect: number;
  skipped: number;
};

export const useGetMultipleChoiceAssessmentAttempts = (
  request: GetMultipleChoiceAssessmentAttemptsRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<
    PaginatedResponse<AssessmentAttemptPreviewModel>
  > {
    const response = await instance.get<
      PaginatedResponse<AssessmentAttemptPreviewModel>
    >(`/v2/subjects/${request.subjectId}/assessments/mc/attempts`, {
      params: {
        fromTime: request.fromTime,
        toTime: request.toTime,
        pageNumber: request.pageNumber,
        pageSize: request.pageSize,
        sort: request.sort,
        assessmentId: request.params?.assessmentId,
        status: request.params?.statuses,
      },
      paramsSerializer: {
        indexes: null,
      },
    });

    return response.data;
  }

  return useQuery({
    queryKey: ['getMultipleChoiceAssessmentAttempts', request],
    queryFn,
    enabled,
  });
};

export type GetMultipleChoiceAssessmentAttemptRequest = {
  subjectId: string;
  assessmentAttemptId: string;
};

export const useGetMultipleChoiceAssessmentAttempt = (
  request: GetMultipleChoiceAssessmentAttemptRequest,
  enabled?: boolean,
) => {
  const { instance } = useAxios();

  async function queryFn(): Promise<
    AssessmentAttemptViewModel<MultipleChoiceQuestionViewModel>
  > {
    const response = await instance.get<
      AssessmentAttemptViewModel<MultipleChoiceQuestionViewModel>
    >(
      `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}`,
    );

    return response.data;
  }

  return useQuery({
    queryKey: [
      'getMultipleChoiceAssessmentAttempt',
      request.subjectId,
      request.assessmentAttemptId,
    ],
    queryFn,
    enabled,
  });
};

export type ExplainQuestionAttemptFromAssessmentRequest = {
  subjectId: string;
  assessmentAttemptId: string;
  questionAttemptId: string;
};

export const useExplainMultipleChoiceQuestionAttemptFromAssessment = () => {
  const { instance } = useAxios();

  async function mutationFn(
    request: ExplainQuestionAttemptFromAssessmentRequest,
  ): Promise<ExplainQuestionAttemptCommandResult> {
    const response = await instance.get<ExplainQuestionAttemptCommandResult>(
      `/v2/subjects/${request.subjectId}/assessments/mc/attempts/${request.assessmentAttemptId}/questions/${request.questionAttemptId}/explain`,
    );

    return response.data;
  }

  return useMutation<
    ExplainQuestionAttemptCommandResult,
    AxiosError<ErrorResponse>,
    ExplainQuestionAttemptFromAssessmentRequest
  >({
    mutationFn,
  });
};
